import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import Page from '../../../components/Page/Page';
import PageBanner from '../../../components/Banner/PageBanner';
import LocumSearchForm from './LocumSearchForm/LocumSearchForm';
import LocumCard from '../../../components/Card/LocumCard';
import PaginationControl from '../../../components/PaginationControl/PaginationControl';
import { useAppSelector } from '../../../redux/store';
import LocumCardSkeleton from '../../../components/Skeletons/LocumCardSkeleton';
import { useLocumSearch } from '../../../context/LocumSearchContext';
import LocumProfile from './LocumProfile';
import clsx from 'clsx';

const FindLocumContent = () => {
  const { loading, page, setPage } = useLocumSearch();

  const { totalPages, locums } = useAppSelector(
    (state) => state.availableLocums
  );

  const [showMobileInfo, setShowMobileInfo] = useState<boolean>(false);

  // Handler for PaginationControl
  const handlePageChange = (newPage: number) => {
    setPage(newPage.toString());
    if (window.innerWidth < 885) {
      setShowMobileInfo(false);
    }
  };

  // parent‐owned mobile flag:
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200);

  // state for card type 2 id transfer
  const [id, setId] = useState('');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1200);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // after your resize useEffect…
  useEffect(() => {
    if (!loading) {
      locums.length > 0 ? setId(locums[0]?.id) : setId('');
    }
  }, [loading, locums]);

  return (
    <Page title="Locumspace | Find a locum">
      <Shell>
        <PageBanner
          label="Find A Locum"
          description="You can find health workers across various professions who are available to work here"
        />
        <LocumSearchForm showMobileInfo={showMobileInfo} />
        {!isMobile ? (
          <Container2>
            <CardDiv>
              {loading ? (
                Array.from({ length: 10 }).map((_, idx) => (
                  <LocumCardSkeleton className="pc" key={idx} />
                ))
              ) : locums.length === 0 ? (
                <Empty>No locums available/match your search</Empty>
              ) : (
                locums.map((locum) => (
                  <LocumCard
                    {...locum}
                    key={locum.id}
                    isMobile={isMobile}
                    clickedIdAction={() => setId(locum.id)}
                    className={clsx(id === locum.id && 'active')}
                  />
                ))
              )}
            </CardDiv>
            <DisplayDiv>
              <LocumProfile locumId={id} />
            </DisplayDiv>
          </Container2>
        ) : (
          <Container>
            {loading ? (
              Array.from({ length: 10 }).map((_, idx) => (
                <LocumCardSkeleton key={idx} />
              ))
            ) : locums.length === 0 ? (
              <Empty>No locums available/match your search</Empty>
            ) : (
              locums.map((locum) => (
                <LocumCard {...locum} key={locum.id} isMobile={isMobile} />
              ))
            )}
          </Container>
        )}
        <PaginationShell>
          <PaginationControl
            endPage={totalPages > 0 ? totalPages : 1}
            changePage={handlePageChange}
            defaultNumOfPagination={5}
            currentPage={parseInt(page, 10)}
          />
        </PaginationShell>
      </Shell>
    </Page>
  );
};

export default FindLocumContent;

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* margin-top: 32px; */
  margin-bottom: 54px;

  @media (max-width: 540px) {
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 10px;
  background-color: ${({ theme }) => theme.palette.white};
  padding: 2rem;
  border-radius: 0.75rem;
  height: 28rem;
  overflow: auto;

  @media (max-width: 860px) {
    flex-direction: column;
    justify-content: start;
    align-items: center;
    padding: 0;
    border-radius: 0;
    background-color: ${({ theme }) => theme.palette.backgroundColor};
    height: 57rem;
    overflow: auto;
    flex-wrap: nowrap;
    gap: 20px;
    padding-bottom: 2rem;
  }

  @media (max-width: 480px) {
    height: 40rem;
    padding-bottom: 0;
  }

  @media (min-width: 1440px) {
    justify-content: space-around;
    align-items: start;
    gap: 20px;
    height: 35rem;
  }
`;

const PaginationShell = styled.div`
  display: grid;
  place-content: center;
  grid-column: span 2;

  @media (max-width: 884px) {
  }
`;

const Container2 = styled.div`
  display: flex;
  justify-content: start;
  align-items: start;
  gap: 10px;
  background-color: ${({ theme }) => theme.palette.white};
  padding: 0.75rem;
  border-radius: 0.75rem;
  height: 28rem;
  overflow: auto;
  overflow-x: hidden;

  @media (max-width: 860px) {
    flex-direction: column;
    justify-content: start;
    align-items: center;
    padding: 0;
    border-radius: 0;
    background-color: ${({ theme }) => theme.palette.backgroundColor};
    height: 57rem;
    overflow: auto;
    flex-wrap: nowrap;
    gap: 20px;
    padding-bottom: 2rem;
  }

  @media (max-width: 480px) {
    height: 40rem;
    padding-bottom: 0;
  }

  @media (min-width: 1440px) {
    justify-content: space-around;
    align-items: start;
    gap: 20px;
    height: 35rem;
  }
`;

const CardDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  gap: 1rem;
`;

const DisplayDiv = styled.div`
  border: 1.4px solid ${({ theme }) => theme.palette.greyGrey3};
  border-radius: 0.25rem;
  padding: 0.75rem;
  flex-grow: 1;
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Empty = styled.div`
  min-width: 300px;
  max-width: 300px;
`;
